import React, { useEffect, useState } from "react";
import { FormControlLabel, Switch, Tooltip } from "@mui/material";
import { truncate } from "utils/string";

import { formatDateTime } from "utils/date";
import usePatients from "pages/patients/usePatients";

import { BOOLEAN_VALUE_MAPPER, TOAST_SEVERITY } from "static/constants";
import * as patientService from "app/services/patient/patient";
import { showToast } from "utils/toast";
import { textFilter } from "utils/table";
import { useDispatch, useSelector } from "react-redux";
import {
  changePatient,
  toggleDeactivate,
} from "app/store/transmission/slice/transmissionSlice";
import NextSendDateRangeFilter from "components/pages/patients/table/components/NextSendDateRangeFilter";
import HospitalColumnFilter from "components/common/filters/hospital/HospitalColumnFilter";
import { checkHasAssignedHospitals } from "utils/transmissionTable";

const usePatientTableColumns = () => {
  const { isUpdatingPatient, updatePatientById } = usePatients();

  const { currentUser } = useSelector((state) => state.auth);

  // Everyone gets the column; only an assigned user gets the tickable list, since there
  // is no list to offer otherwise.
  const hasAssignedHospitals = checkHasAssignedHospitals(currentUser);
  const [deviceAlertList, setDeviceAlertList] = useState([]);
  const dispatch = useDispatch();

  const fetchDeviceAlertList = async (filter = {}) => {
    try {
      const data = await patientService.fetchDeviceAlertList(filter);
      data.unshift("All");

      setDeviceAlertList(data);
    } catch (err) {
      showToast({ message: err.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  useEffect(() => {
    fetchDeviceAlertList({ active: true });
  }, []);

  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      filterVariant: "text",
      filterFn: textFilter,
      // Identifies the patient, so the column picker always keeps it on.
      enableHiding: true,
      Cell: ({ cell }) => (
        <Tooltip title={cell?.getValue()} placement="bottom">
          <div>{truncate(cell?.getValue(), 27)}</div>
        </Tooltip>
      ),
    },
    {
      header: "Account",
      accessorKey: "account",
      filterVariant: "text",
      filterFn: textFilter,
      enableHiding: true,
    },
    {
      header: "DOB",
      accessorKey: "dob",
      filterVariant: "date",
      filterFn: textFilter,
      enableHiding: true,
      Cell: ({ cell }) =>
        cell?.getValue() ? formatDateTime(cell?.getValue()) : "—",
    },
    {
      header: "DOI",
      accessorKey: "doi",
      filterVariant: "date",
      filterFn: textFilter,
      Cell: ({ cell }) =>
        cell?.getValue() ? formatDateTime(cell?.getValue()) : "—",
    },
    {
      header: "Manufacturer",
      accessorKey: "manufacturer",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Evoke ID",
      accessorKey: "evoke_id",
      filterVariant: "text",
      filterFn: textFilter,
      enableHiding: true,
    },
    {
      header: "MRN",
      accessorKey: "mrn",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ cell }) =>
        cell?.getValue()?.length > 0 ? cell?.getValue() : "—",
    },
    {
      header: "Hospital",
      accessorKey: "hospital",
      ...(hasAssignedHospitals
        ? {
            Filter: (props) => (
              <HospitalColumnFilter {...props} includeUnassigned />
            ),
          }
        : { filterVariant: "text", filterFn: textFilter }),
      enableSorting: false,
      // Blank, not a dash: no hospital is a gap in the record rather than a value.
      Cell: ({ cell }) => cell?.getValue() || "",
    },
    {
      header: "Last Billing",
      accessorKey: "lastBillDate",
      filterVariant: "date",
      filterFn: textFilter,
      Cell: ({ cell }) =>
        cell?.getValue() ? formatDateTime(cell?.getValue(), "MM/DD/YY") : "—",
    },
    {
      header: "Next Send",
      accessorKey: "nextSend",
      filterVariant: "custom",
      filterFn: textFilter,
      Filter: NextSendDateRangeFilter,
      Cell: ({ cell }) =>
        cell?.getValue() ? formatDateTime(cell?.getValue(), "MM/DD/YY") : "—",
    },
    {
      header: "Phone Numbers",
      accessorKey: "phoneNumbers",
      filterVariant: "text",
      enableSorting: false,
      filterFn: textFilter,
      Cell: ({ cell }) => {
        const phoneNumbers = cell.getValue();

        if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) {
          return "—";
        }

        return (
          <div className="d-flex flex-column justify-center align-items-center g-1">
            {phoneNumbers?.map((phone, index) => (
              <div key={index}>{phone}</div>
            ))}
          </div>
        );
      },
    },
    {
      header: "Email",
      accessorKey: "email",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ row }) => {
        const email = row?.original?.information?.email || "-";
        return (
          <Tooltip title={email} placement="bottom">
            <div>{email}</div>
          </Tooltip>
        );
      },
    },
    {
      header: "Physician",
      accessorKey: "physicians",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ cell }) => cell?.getValue()?.join(", "),
    },
    {
      header: "Leads",
      accessorKey: "leadModel",
      filterVariant: "text",
      enableSorting: false,
      filterFn: textFilter,
      Cell: ({ row }) => {
        const leads = row?.original?.deviceInformation?.leads || [];

        if (!Array.isArray(leads) || leads.length === 0) {
          return "—";
        }

        return (
          <div className="d-flex flex-column justify-center align-items-center g-1">
            {leads?.map((lead, index) => (
              <div key={index}>{lead}</div>
            ))}
          </div>
        );
      },
    },
    {
      header: "Device",
      accessorKey: "device",
      filterVariant: "text",
      filterFn: textFilter,
      accessorFn: (rowData) => {
        const previousDevices = rowData?.previousDevices
          ?.map((device) => device?.device_model)
          ?.reverse();

        const devicesHtml = (
          <div style={{ whiteSpace: "pre-line", margin: "10px" }}>
            <div style={{ fontWeight: "bold" }}>Current Device:</div>
            <div>{rowData?.device}</div>
            <div style={{ fontWeight: "bold", marginTop: "10px" }}>
              Previous Devices:
            </div>
            <ul
              className="trans-table__alertList"
              style={{ marginLeft: "16px" }}
            >
              {previousDevices?.map((device, index) => (
                <li key={index}>{device}</li>
              ))}
            </ul>
          </div>
        );

        return previousDevices?.length > 0 ? (
          <Tooltip title={devicesHtml} placement="bottom">
            <div>{rowData?.device}</div>
          </Tooltip>
        ) : (
          <div>{rowData?.device || "—"}</div>
        );
      },
    },
    {
      header: "Patient Alert",
      accessorKey: "patientAlert",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ row }) =>
        row?.original?.patientAlertInformation?.alertPatient &&
        row?.original?.patientAlertInformation?.alertPatientText?.length > 0
          ? row?.original?.patientAlertInformation?.alertPatientText
          : "—",
    },
    {
      header: "Device Alert",
      accessorKey: "deviceAlert",
      filterVariant: "select",
      filterFn: textFilter,
      filterSelectOptions: deviceAlertList?.map((data) => ({
        value: data === "All" ? "" : data,
        label: data,
      })),
      Cell: ({ row }) =>
        row?.original?.deviceInformation?.alertDevice &&
        row?.original?.deviceInformation?.alertDeviceText?.length > 0
          ? row?.original?.deviceInformation?.alertDeviceText
          : "—",
    },

    {
      header: "Subscribe for Call/SMS",
      accessorKey: "subscribeForCallOrSms",
      filterVariant: "select",
      filterFn: textFilter,
      enableSorting: false,
      filterSelectOptions: [
        { label: "All", value: "" },
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ],
      Cell: ({ row }) => {
        const rowData = row?.original;
        const isSubscribed = rowData?.subscribe_setting?.subscribe;
        return isSubscribed !== undefined && isSubscribed !== null
          ? BOOLEAN_VALUE_MAPPER[isSubscribed]
          : "—";
      },
    },
    {
      header: "Active",
      accessorKey: "active",
      filterVariant: "select",
      filterFn: textFilter,
      filterSelectOptions: [
        { label: "All", value: "" },
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
      enableSorting: false,
      accessorFn: (rowData) => {
        const { _id, active, account } = rowData;
        return (
          <FormControlLabel
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const isActive = e.target.checked;

              if (!isActive) {
                dispatch(
                  toggleDeactivate({
                    deactivateFrom: "table",
                    isDeactivate: !isActive,
                  })
                );
                dispatch(
                  changePatient({
                    patientId: _id,
                    account,
                  })
                );
              } else {
                const data = { active: isActive };
                updatePatientById(_id, data);
              }
            }}
            disabled={isUpdatingPatient}
            control={<Switch defaultChecked={active} />}
          />
        );
      },
    },
  ];

  return { columns };
};

export default usePatientTableColumns;
